// The rhythm the bedside monitor draws. An observations card gives a heart rate but no rhythm, so the trace is sinus
// at that rate unless the question itself states the patient's rhythm: a stem that says "ECG: AF with rapid
// ventricular response" must not sit above a monitor drawing P waves. Only plain statements of the current rhythm
// count ("ECG: AF", "irregularly irregular pulse", "fast AF", "in AF", "broad complex tachycardia"); history
// ("known AF", "paroxysmal AF") and negations ("no AF", "not in AF", "AF was excluded") leave the trace sinus.
// A rhythm on the graphic itself, should the content ever carry one, wins.
import type { ObsGraphic, Rhythm } from '@shared/types';

/** Rhythms a patient with recordable observations can be drawn in (arrest rhythms never sit on an obs card). */
const DRAWABLE: ReadonlySet<Rhythm> = new Set<Rhythm>(['sinus', 'sinusTachy', 'sinusBrady', 'af', 'vt']);

const AF: readonly RegExp[] = [
  /\birregularly irregular\b/i,
  /\bECG\b[^.;?]{0,40}?\b(?:AF|atrial fibrillation)\b/i,
  /\b(?:fast|rapid|new|new[- ]onset|uncontrolled)\s+(?:AF|atrial fibrillation)\b/i,
  /\b(?:AF|atrial fibrillation)\s+with\s+(?:an?\s+)?(?:rapid|fast|uncontrolled)\b/i,
  /\bin\s+(?:AF|atrial fibrillation)\b/i,
];
const VT: readonly RegExp[] = [
  /\bECG\b[^.;?]{0,40}?\b(?:VT|ventricular tachycardia|broad[- ]complex tachycardia)\b/i,
  /\bbroad[- ]complex tachycardia\b/i,
  /\b(?:pulsed|sustained|monomorphic)\s+(?:VT|ventricular tachycardia)\b/i,
];
const NEGATION = /\b(?:no|not|without|nor|never|absent)\b/i;
const NEGATED_AFTER = /^[^.;?]{0,24}?\b(?:excluded|ruled out|unlikely)\b/i;

/** True when the text states the rhythm, in a clause that does not deny it. */
function states(text: string, patterns: readonly RegExp[]): boolean {
  for (const re of patterns) {
    const m = re.exec(text);
    if (!m) continue;
    const end = m.index + m[0].length;
    const clauseStart = Math.max(text.lastIndexOf('.', m.index - 1), text.lastIndexOf(';', m.index - 1), text.lastIndexOf('?', m.index - 1), text.lastIndexOf(',', m.index - 1)) + 1;
    if (NEGATION.test(text.slice(clauseStart, end)) || NEGATED_AFTER.test(text.slice(end))) continue;
    return true;
  }
  return false;
}

const byRate = (rate: number): Rhythm => (rate > 100 ? 'sinusTachy' : rate < 60 ? 'sinusBrady' : 'sinus');
/** Rates the synthesiser draws each rhythm at; outside them the trace would disagree with the HR on the screen. */
const fits = (r: Rhythm, rate: number): boolean => (r === 'af' ? rate >= 40 && rate <= 220 : r === 'vt' ? rate >= 120 && rate <= 260 : rate >= 20 && rate <= 250);

export function monitorRhythm(o: ObsGraphic, stem?: string): Rhythm {
  const rate = typeof o.hr === 'number' && Number.isFinite(o.hr) ? o.hr : 75;
  const own = (o as ObsGraphic & { rhythm?: unknown }).rhythm;
  if (typeof own === 'string' && DRAWABLE.has(own as Rhythm) && fits(own as Rhythm, rate)) return own as Rhythm;
  const text = (stem ?? '').trim();
  if (text) {
    if (states(text, VT) && fits('vt', rate)) return 'vt';
    if (states(text, AF) && fits('af', rate)) return 'af';
  }
  return byRate(rate);
}
